import Chart, { useChart } from 'src/components/chart';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { t } from 'i18next';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { fPercent } from 'src/utils/format-number';
import { generateAnnotations } from 'src/utils/graph-demo-annotations';
import { formatCurrency } from 'src/utils/format-currency';
import { useEffect, useRef } from 'react';

// Types
type Props = {
  series: {
    name: string;
    type: string;
    data: number[];
  }[];
  labels: string[];
  onLegendClick?: (seriesName: string) => void;
  height?: number;
  showDataLabels?: boolean;
  // Rounded-corner radius for the bars. Pass 0 to disable rounding — useful when
  // the top stacked segment is $0, which ApexCharts otherwise renders as a
  // sub-radius stub rounded on the wrong edge.
  barBorderRadius?: number;
  // Hide the built-in ApexCharts legend (use when rendering a custom legend
  // outside the chart, e.g. in the card header).
  hideLegend?: boolean;
};

// Constants
const CHART_COLORS = {
  TWO_SERIES: ['#ff9800', '#5119b7'],
  MULTIPLE_SERIES: ['#5119b7', '#ffb300', '#ffd54f', '#8E33FF']
};

const PERCENTAGE_SERIES_INDICATORS = ['CTR', 'vulnerability'];
const AD_COST_SERIES_NAMES = [
  'dashboard.brandTraffic.adCost',
  'dashboard.brandTraffic.searchAdCost',
  'dashboard.brandTraffic.shoppingAdCost',
  'dashboard.global.pmax_ad_cost'
];

const DEFAULT_HEIGHT = 450;
const MAX_PERCENTAGE = 100;
const Y_AXIS_MARGIN_MULTIPLIER = 1.3;

// Utility functions
const isPercentageData = (name: string): boolean => {
  return PERCENTAGE_SERIES_INDICATORS.some(indicator => name.includes(indicator));
};

const limitTo100 = (value: number): number => Math.min(value, MAX_PERCENTAGE);

// Function to round up to nice intervals for Y-axis
const roundUpToNiceInterval = (value: number): number => {
  if (value <= 0) return 0.5;

  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;

  let niceValue;
  if (normalized <= 1.2) niceValue = 1.2;
  else if (normalized <= 1.5) niceValue = 1.5;
  else if (normalized <= 2) niceValue = 2;
  else if (normalized <= 3) niceValue = 3;
  else if (normalized <= 5) niceValue = 5;
  else if (normalized <= 7) niceValue = 7;
  else niceValue = 10;

  return niceValue * magnitude;
};

const getAdCostSeries = (series: any[]) => {
  return series.filter(serie =>
    AD_COST_SERIES_NAMES.some(name => serie.name === t(name))
  );
};

const calculateMaxAdCost = (adCostSeries: any[]): number => {
  if (!adCostSeries.length) return 0.5;

  // Separate bar series (stacked) from line series (not stacked)
  const barSeries = adCostSeries.filter(serie => serie.type === 'bar');
  const lineSeries = adCostSeries.filter(serie => serie.type === 'line' || !serie.type);

  let maxValue = 0;

  // For bar series (stacked), calculate the maximum sum at each data point
  if (barSeries.length > 0) {
    const maxStackedSum = Math.max(...Array.from({ length: barSeries[0].data.length }, (_, i) =>
      barSeries.reduce((sum, serie) => sum + (serie.data[i] || 0), 0)
    ));
    maxValue = Math.max(maxValue, maxStackedSum);
  }

  // For line series (not stacked), find the maximum individual value
  if (lineSeries.length > 0) {
    const maxLineValue = Math.max(...lineSeries.flatMap(serie =>
      serie.data.filter((value: number) => value !== null && value !== undefined)
    ));
    maxValue = Math.max(maxValue, maxLineValue);
  }

  // If no valid data found, return default
  if (maxValue === 0) return 0.5;

  // Apply margin multiplier and round to nice interval
  const maxWithMargin = maxValue * Y_AXIS_MARGIN_MULTIPLIER;
  return roundUpToNiceInterval(maxWithMargin);
};

const getChartColors = (seriesCount: number): string[] => {
  return seriesCount === 2 ? CHART_COLORS.TWO_SERIES : CHART_COLORS.MULTIPLE_SERIES;
};

// Auto-scale the Blended CTR axis to the data (capped at 100%) instead of
// always spanning 0–100%, so the line uses the full chart height.
const calculateMaxCtr = (series: any[]): number => {
  const ctrSeries = series.find((serie) => serie.name === t('dashboard.global.blended_ctr'));
  const values = (ctrSeries?.data ?? []).filter(
    (value: number) => value !== null && value !== undefined
  );
  if (!values.length) return MAX_PERCENTAGE;

  const maxValue = Math.max(...values);
  if (!Number.isFinite(maxValue) || maxValue <= 0) return MAX_PERCENTAGE;

  return Math.min(MAX_PERCENTAGE, roundUpToNiceInterval(maxValue * Y_AXIS_MARGIN_MULTIPLIER));
};

// Formats a value according to its series' unit: percentage series → %, Blended CPC →
// fine-grained currency (3 decimals), ad-cost series → compact currency (`adCostDecimals`).
// Single source of the per-series unit rule, shared by the tooltip and the data labels.
const formatSeriesValue = (
  seriesName: string,
  value: number,
  workspace: any,
  lang: string,
  adCostDecimals: number
): string => {
  if (isPercentageData(seriesName)) return fPercent(limitTo100(value));
  if (seriesName === t('dashboard.global.blended_cpc')) {
    return formatCurrency(value, workspace?.currency || 'USD', lang, false, 3);
  }
  return formatCurrency(value, workspace?.currency || 'USD', lang, true, adCostDecimals);
};

const createTooltipFormatter = (workspace: any, lang: string) => {
  return (value: number, { seriesIndex, w }: any) => {
    if (typeof value === 'undefined') return value;

    const seriesName = w.config.series[seriesIndex].name;
    return formatSeriesValue(seriesName, value, workspace, lang, 1);
  };
};

const createYAxisConfig = (adjustedSeries: any[], workspace: any, lang: string) => {
  const hasCtr = adjustedSeries.some(
    (serie) => serie.name === t('dashboard.global.blended_ctr')
  );

  const hasAdCost = adjustedSeries.some((serie) =>
    AD_COST_SERIES_NAMES.map((name) => t(name)).includes(serie.name)
  );

  const hasCpc = adjustedSeries.some(
    (serie) => serie.name === t('dashboard.global.blended_cpc')
  );

  const yAxis: any[] = [];

  if (hasCtr) {
    yAxis.push({
      title: { text: t('dashboard.global.blended_ctr') },
      seriesName: [t('dashboard.global.blended_ctr')],
      opposite: true,
      min: 0,
      max: calculateMaxCtr(adjustedSeries),
      labels: {
        formatter(value: number) {
          return `${limitTo100(value).toFixed(0)}%`;
        },
      },
    });
  }

  if (hasAdCost) {
    yAxis.push({
      title: { text: t('dashboard.global.ad_cost') },
      seriesName: AD_COST_SERIES_NAMES.map((name) => t(name)),
      min: 0,
      max: calculateMaxAdCost(getAdCostSeries(adjustedSeries)),
      labels: {
        formatter(value: number) {
          return formatCurrency(value, workspace?.currency || 'USD', lang, true, 1);
        },
      },
    });
  }

  if (hasCpc) {
    yAxis.push({
      title: { text: t('dashboard.global.blended_cpc') },
      seriesName: [t('dashboard.global.blended_cpc')],
      min: 0,
      max: (() => {
        const cpcSeries = adjustedSeries.find(
          (serie) => serie.name === t('dashboard.global.blended_cpc')
        );

        if (!cpcSeries?.data?.length) return 0.5;

        return roundUpToNiceInterval(
          Math.max(...cpcSeries.data) * Y_AXIS_MARGIN_MULTIPLIER
        );
      })(),
      show: false,
      labels: {
        formatter(value: number) {
          return formatCurrency(value, workspace?.currency || 'USD', lang, false, 2);
        },
      },
    });
  }

  return yAxis;
};

export default function ChartMixed({ series, labels, onLegendClick, height = DEFAULT_HEIGHT, showDataLabels = false, barBorderRadius = 4, hideLegend = false }: Props) {
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const workspace = useCurrentWorkspace();
  const annotations = generateAnnotations(workspace, filterStore, labels);
  const chartRef = useRef(null);
  const lang: any = localStorage.getItem('i18nextLng');

  // Adjust series data to limit percentage values to 100%
  const adjustedSeries = series.map((serie) => ({
    ...serie,
    data: serie.data.map((value) => isPercentageData(serie.name) ? limitTo100(value) : value),
  }));

  // Honor per-series colors (Figma palette) when every series provides one,
  // otherwise fall back to the default chart palette.
  const seriesColors = adjustedSeries.map((serie: any) => serie.color).filter(Boolean);
  const resolvedColors =
    seriesColors.length === adjustedSeries.length
      ? seriesColors
      : getChartColors(adjustedSeries.length);

  const chartOptions = useChart({
    plotOptions: {
      bar: {
        columnWidth: '65%',
        borderRadius: barBorderRadius,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
        // Segment labels sit in the middle of each bar, away from the lines and
        // their labels that run near the bar tops.
        dataLabels: { position: 'center' },
      },
    },
    dataLabels: showDataLabels
      ? {
        // Labels on every series: the ad-cost bars and the CTR/CPC lines.
        enabled: true,
        formatter(value: number, opts: any) {
          // Read the real per-segment value to avoid stacked-bar label/value drift.
          const serie = opts?.w?.config?.series?.[opts.seriesIndex];
          const raw = serie?.data?.[opts.dataPointIndex] ?? value;
          if (!raw || raw <= 0) return '';
          const name = serie?.name ?? '';
          const label = formatSeriesValue(name, raw, workspace, lang, 0);
          if (name === t('dashboard.global.blended_cpc')) {
            // The leading blank line renders the CPC label below its point while the
            // CTR label stays above — the two lines often run at the same height.
            return ['', label];
          }
          return label;
        },
        style: {
          fontSize: '11px',
          fontWeight: 600,
          fontFamily: 'Public Sans, sans-serif',
          colors: ['#1c252e'],
        },
        dropShadow: { enabled: false },
        background: { enabled: false },
      }
      : { enabled: false },
    chart: {
      stacked: true,
      height,
      events: {
        mounted: (chartContext: any) => {
          try {
            if (chartContext?.hideSeries) {
              chartContext.hideSeries(t('dashboard.global.blended_cpc'));
            }
          } catch (e) {
            console.warn("Impossible de cacher la série blended_cpc :", e);
          }
        }
      },
    },
    fill: {
      colors: resolvedColors
    },
    // No stroke on bars (the theme default of 3px would paint a visible sliver
    // even on a $0 bar); the line series (Blended CTR/CPC) use the design's 2px.
    stroke: {
      width: adjustedSeries.map((serie: any) => (serie.type === 'bar' ? 0 : 2)),
    },
    colors: resolvedColors,
    labels: labels || [],
    markers: {
      size: 0
    },
    legend: {
      show: !hideLegend,
      position: 'top',
      horizontalAlign: 'right',
      fontSize: '13px',
      itemMargin: { horizontal: 6 },
      markers: {
        customHTML() {
          return '<div style="width: 8px; height: 8px; border-radius: 50%; background-color: currentcolor;"></div>';
        },
      },
      labels: { colors: '#637381' },
    },
    // Give the x-axis date labels a little breathing room (gap above + below)
    // so they don't sit cramped against the bars / card edge.
    xaxis: {
      labels: { offsetY: 4 },
    },
    grid: {
      padding: { bottom: 8 },
    },
    yaxis: createYAxisConfig(adjustedSeries, workspace, lang),
    tooltip: {
      shared: true,
      intersect: false,
      custom: function ({ series, dataPointIndex, w }) {
        if (!series || !w.globals.seriesNames || !w.globals.colors) {
          return '';
        }

        const date = labels[dataPointIndex] || '';
        const formatter = createTooltipFormatter(workspace, lang);
        const collapsed = [
          ...(w.globals.collapsedSeriesIndices || []),
          ...(w.globals.ancillaryCollapsedSeriesIndices || []),
        ];

        const rows = series.map((s: any, i: any) => {
          if (collapsed.includes(i)) return '';
          const value = s[dataPointIndex];
          const formattedValue = formatter(value, { seriesIndex: i, w });
          return `
            <div style="display:flex;align-items:center;gap:8px;padding-left:4px;">
              <span style="display:inline-block;width:2px;height:12px;border-radius:2px;background-color:${w.globals.colors[i]};flex-shrink:0;"></span>
              <div style="flex:1;display:flex;justify-content:space-between;align-items:center;font-family:Public Sans,sans-serif;font-size:13px;font-weight:500;color:#333c47;white-space:nowrap;gap:16px;">
                <span>${w.globals.seriesNames[i]}</span>
                <span>${formattedValue ?? ''}</span>
              </div>
            </div>
          `;
        }).join('');

        return `
          <div style="background:#fcfcfd;border:1px solid rgba(42,50,59,0.2);border-radius:12px;box-shadow:1px 2px 5px rgba(0,0,0,0.06);overflow:hidden;min-width:240px;">
            <div style="padding:12px 12px 0 12px;">
              <p style="font-family:Public Sans,sans-serif;font-size:14px;font-weight:700;color:#333c47;white-space:nowrap;margin:0;">${date}</p>
            </div>
            <div style="padding:12px;display:flex;flex-direction:column;gap:6px;">
              ${rows}
            </div>
          </div>
        `;
      },
      y: {
        formatter: createTooltipFormatter(workspace, lang),
      },
    },
    annotations: {
      xaxis: annotations
    },
  });

  return (
    <Chart
      ref={chartRef}
      dir="ltr"
      type="line"
      series={adjustedSeries}
      options={chartOptions}
      sx={{
        // Full width: the legend is right-aligned, so the card's own 24px padding
        // sets its exact gap to the edge.
        width: '100%',
        mt: .5,
        '& .apexcharts-legend-group-vertical': {
          flexDirection: 'row',
        },
      }}
      height={height}
    />
  );
}  
